import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Button,
  Form,
  Input,
  Message,
  Modal,
} from 'semantic-ui-react';

import ConsoleErrorMessage from '../utils/console-error-message';

import { errorType, keyType } from '../types';

import {
  clearError, closeModal, deleteKey, putKey, renameKey,
} from '../../modules/keys';

/**
 * This timeout is to give the modal time to mount to the DOM.
 * Otherwise it will be called .focus() on an undefined object.
 */
const SET_REF_TIMEOUT_MS = 10;

export class KeyAuthModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      password: '',
    };
    this.ref = null;
  }

  componentDidMount() {
    const { connectedClearError } = this.props;
    connectedClearError();
  }

  componentDidUpdate(prevProps) {
    const { keyAuthOpen } = this.props;
    if (prevProps.keyAuthOpen === false && keyAuthOpen === true) {
      this.focusOnPasswordInput();
    }
  }

  setRef = (ref) => {
    this.ref = ref;
  }

  focusOnPasswordInput = () => {
    setTimeout(() => {
      if (this.ref) {
        this.ref.focus();
      }
    }, SET_REF_TIMEOUT_MS);
  }

  /*
   * NOTE: This is quite hacky; basically implicit form submission
   * -- when you hit enter to submit the form -- would work if there
   * is only one field in the modal, but with two fields it doesn't.
   * It doesn't matter which of the two I remove.
   *
   * Therefore we set the onKeyPress prop of the form...
   */
  handleKeyPress = (e) => {
    const { deleteKeyRequestOut, putKeyRequestOut } = this.props;
    const { password } = this.state;
    const requestOut = deleteKeyRequestOut || putKeyRequestOut;
    if (!requestOut && !!password && e.key === 'Enter') {
      this.handleSubmit(e);
    }
  }

  handleSubmit = (event) => {
    const { keyAuthAction, keyAuthKey } = this.props;
    const { password } = this.state;
    event.preventDefault();
    if (keyAuthAction === 'keyDelete') {
      const { connectedDeleteKey } = this.props;
      connectedDeleteKey({ id: keyAuthKey.id, password })
        .then(() => {
          const { error } = this.props;
          if (!error) { this.handleModalClose(); }
        });
    }
    if (keyAuthAction === 'keyPut') {
      const { connectedPutKey } = this.props;
      connectedPutKey({ id: keyAuthKey.id, name: keyAuthKey.name, password })
        .then(() => {
          const { error } = this.props;
          if (!error) { this.handleModalClose(); }
        });
    }
  }

  handleKeyNameChange = (event) => {
    const { connectedRenameKey, keyAuthKey } = this.props;
    event.preventDefault();
    connectedRenameKey(event.target.value, keyAuthKey);
  }

  handlePasswordChange = (event) => {
    const { connectedClearError } = this.props;
    event.preventDefault();
    connectedClearError();
    this.setState({
      password: event.target.value,
    });
  }

  handleModalClose = () => {
    const { connectedCloseModal, connectedClearError } = this.props;
    connectedClearError();
    this.setState({
      password: '',
    });
    connectedCloseModal();
  }

  renderKeyNameField = () => {
    const { keyAuthKey } = this.props;
    return (
      <Input
        className="re is-marginTop-1em"
        fluid
        onChange={this.handleKeyNameChange}
        onKeyPress={this.handleKeyPress}
        placeholder="Key name"
        type="text"
        value={keyAuthKey.name || ''}
      />
    );
  }

  renderVerifyFailed = () => {
    const { error } = this.props;
    return (
      <Message error>
        <ConsoleErrorMessage error={error} showSignIn={false} />
      </Message>
    );
  }

  render() {
    const {
      deleteKeyRequestOut, email, error, keyAuthAction, keyAuthOpen, putKeyRequestOut,
    } = this.props;
    const { password } = this.state;
    const requestOut = deleteKeyRequestOut || putKeyRequestOut;
    return (
      <Modal
        className="autofocus"
        closeOnDimmerClick={false}
        open={keyAuthOpen}
        size="tiny"
      >
        <Modal.Header content="Confirm API Keys Actions" />
        <Modal.Content>
          <p>
            Enter your password to confirm action.
          </p>
          <Form onKeyPress={this.handleKeyPress} onSubmit={this.handleSubmit}>
            <div className="re is-marginBottom-1em">
              {`Currently signed in as: ${email}`}
              <Input
                className="re is-display-none key-action-email"
                readOnly
                transparent
                type="text"
                value={email}
              />
            </div>
            <Form.Field>
              <Input
                onChange={this.handlePasswordChange}
                placeholder="Password"
                ref={this.setRef}
                type="password"
                value={password}
              />
            </Form.Field>
          </Form>
          { keyAuthAction === 'keyPut' && this.renderKeyNameField() }
          { error && !requestOut
              && this.renderVerifyFailed()
          }
        </Modal.Content>
        <Modal.Actions>
          <Button
            content="Cancel"
            onClick={this.handleModalClose}
            type="button"
          />
          <Button
            content="Confirm"
            disabled={!password || requestOut}
            loading={requestOut}
            onClick={this.handleSubmit}
            primary
            type="submit"
          />
        </Modal.Actions>
      </Modal>
    );
  }
}

KeyAuthModal.propTypes = {
  connectedCloseModal: PropTypes.func.isRequired,
  connectedDeleteKey: PropTypes.func.isRequired,
  connectedPutKey: PropTypes.func.isRequired,
  connectedRenameKey: PropTypes.func.isRequired,
  connectedClearError: PropTypes.func.isRequired,
  deleteKeyRequestOut: PropTypes.bool.isRequired,
  email: PropTypes.string.isRequired,
  error: errorType,
  keyAuthAction: PropTypes.string.isRequired,
  keyAuthKey: keyType.isRequired,
  keyAuthOpen: PropTypes.bool.isRequired,
  putKeyRequestOut: PropTypes.bool.isRequired,
};

KeyAuthModal.defaultProps = {
  error: undefined,
};

const mapStateToProps = state => ({
  deleteKeyRequestOut: state.keys.deleteKeyRequestOut,
  email: state.session.email,
  error: state.keys.error,
  keyAuthAction: state.keys.keyAuthAction,
  keyAuthKey: state.keys.keyAuthKey,
  keyAuthOpen: state.keys.keyAuthOpen,
  putKeyRequestOut: state.keys.putKeyRequestOut,
});

const mapDispatchToProps = dispatch => bindActionCreators({
  connectedClearError: clearError,
  connectedCloseModal: closeModal,
  connectedDeleteKey: deleteKey,
  connectedPutKey: putKey,
  connectedRenameKey: renameKey,
}, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(KeyAuthModal);
